import {buildErrorPayload, errorToPlainObject} from './errorPayload';
import {buildEmailLogPayload, createErrorTransport} from './errorTransport';
import {
  emailLogPath,
  instrumentBrowserRuntime,
  isCanceledError,
  isNetworkConnectivityError,
} from './browserInstrumentation';

const frontendLoggingTarget = {
  emailLog: 'emailLog',
  lokiLog: 'lokiLog',
};
const frontendErrorsPath = '/frontend-errors';
const recoveryPopupIgnoredTypes = ['resource-error'];
let endpointUrl = null;
let initialized = false;
let transport = null;

function isErrorReportingEnabled() {
  if (!document.kvConfig) {
    return false;
  }

  return document.kvConfig.frontendErrorReportingEnabled !== false
    && document.kvConfig.frontendErrorReportingEnabled !== 'false';
}

function isLocalDevHost() {
  return window.location.port === '9007'
    || window.location.hostname === 'localhost'
    || window.location.hostname === '127.0.0.1'
    || (document.kvConfig && document.kvConfig.devMode);
}

function getFrontendLoggingTarget() {
  if (!document.kvConfig || !document.kvConfig.frontendLoggingTarget) {
    return frontendLoggingTarget.emailLog;
  }

  return document.kvConfig.frontendLoggingTarget;
}

function getLokiLogEndpointConfig() {
  if (document.kvConfig.frontendLoggingUrl) {
    return {
      url: document.kvConfig.frontendLoggingUrl,
    };
  }

  if (isLocalDevHost()) {
    return {
      url: 'http://localhost:3010' + frontendErrorsPath,
    };
  }

  return null;
}

function getDefaultEmailLogUrl() {
  if ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    && document.kvConfig.domain) {
    return document.kvConfig.domain.replace(/\/$/, '') + emailLogPath;
  }

  return emailLogPath;
}

function getEmailLogEndpointConfig() {
  return {
    url: document.kvConfig.frontendEmailLogUrl || document.kvConfig.frontendLoggingUrl || getDefaultEmailLogUrl(),
    formatPayload: buildEmailLogPayload,
  };
}

function getEndpointConfig() {
  if (!isErrorReportingEnabled()) {
    return null;
  }

  if (getFrontendLoggingTarget() === frontendLoggingTarget.lokiLog) {
    return getLokiLogEndpointConfig();
  }

  return getEmailLogEndpointConfig();
}

function shouldCaptureResourceErrors() {
  return !!(document.kvConfig && document.kvConfig.frontendLoggingResourceErrors === true);
}

function shouldLogErrorsToConsole() {
  if (document.kvConfig && document.kvConfig.frontendErrorConsoleLoggingEnabled !== undefined) {
    return document.kvConfig.frontendErrorConsoleLoggingEnabled !== false
      && document.kvConfig.frontendErrorConsoleLoggingEnabled !== 'false';
  }

  return isLocalDevHost();
}

function logFatalErrorToConsole(type, error, payload) {
  if (!shouldLogErrorsToConsole() || typeof console === 'undefined' || !console.error) {
    return;
  }

  console.error('[visualizer fatal error]', type, error, payload);
}

function showRecoveryPopup() {
  try {
    const {showFatalErrorRecoveryPopup} = require('./errorRecoveryPopup');

    showFatalErrorRecoveryPopup();
  } catch (error) {
    // Early bootstrap failures can happen before the React/Redux recovery UI is ready.
  }
}

function handleNetworkConnectivityError() {
  try {
    const {handleNetworkConnectionError} = require('./errorRecoveryPopup');

    handleNetworkConnectionError();
  } catch (error) {
    // Early bootstrap failures can happen before the React/Redux recovery UI is ready.
  }
}

export function initializeErrorReporting() {
  if (initialized) {
    return;
  }

  const endpointConfig = getEndpointConfig();

  if (!endpointConfig) {
    return;
  }

  endpointUrl = endpointConfig.url;
  initialized = true;
  transport = createErrorTransport(endpointUrl, {
    formatPayload: endpointConfig.formatPayload,
  });
  window.__visualizerErrorReporter = {
    reportFatalError,
    reportNonFatalError,
  };

  instrumentBrowserRuntime({
    reportFatalError,
    captureResourceErrors: shouldCaptureResourceErrors(),
  });
}

export function instrumentReduxStore() {
  initializeErrorReporting();
}

export function reportFatalError(type, error, extra) {
  reportError(type, error, extra, true);
}

export function reportNonFatalError(type, error, extra) {
  initializeErrorReporting();
  reportError(type, error, extra, false);
}

function reportError(type, error, extra, shouldShowRecoveryPopup) {
  if (isCanceledError(error)) {
    return;
  }

  if (isNetworkConnectivityError(error)) {
    if (shouldShowRecoveryPopup) {
      handleNetworkConnectivityError();
    }
    return;
  }

  const errorData = errorToPlainObject(error);
  const payload = buildErrorPayload({
    type,
    errorData,
    extra: extra || {},
  });

  logFatalErrorToConsole(type, error, payload);

  if (transport) {
    transport.send(payload);
  }

  if (shouldShowRecoveryPopup && recoveryPopupIgnoredTypes.indexOf(type) === -1) {
    showRecoveryPopup();
  }
}
